import { useState, useMemo, useEffect } from "react";
import { Calendar } from "@/components/ui/calendar";
import { fr } from "date-fns/locale";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CalendarDays } from "lucide-react";
import { formatDatesFr } from "@/lib/shariti";

const MONTHS_FR = ["janvier", "février", "mars", "avril", "mai", "juin",
  "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
// Version SANS accent (comparaison robuste : « aout » == « août »).
const MONTHS_NOACC = MONTHS_FR.map((m) =>
  m.normalize("NFD").replace(/[\u0300-\u036f]/g, ""));

const stripAccents = (s) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
// Séparateurs de PLAGE : « au », « jusqu'au », tirets (-, –, —).
const RANGE_RE = /jusqu'?au|\bau\b|[-\u2013\u2014]/i;

function monthsInText(low) {
  const found = [];
  for (let i = 0; i < 12; i++) {
    const pos = low.indexOf(MONTHS_NOACC[i]);
    if (pos >= 0) found.push({ i, pos });
  }
  found.sort((a, b) => a.pos - b.pos);
  return found.map((f) => f.i);
}

// Parseur du texte FR. Gère les LISTES (« lundi 3, mardi 4 et mercredi 5 août 2026 »)
// ET les PLAGES écrites en toutes lettres (« du 3 au 5 août », « 6 – 8 août »,
// « 30 juillet au 2 août 2026 ») — sans quoi seule la 1re journée était retenue.
// Retourne un tableau d'objets Date. Insensible aux accents.
function parseDatesFr(text) {
  if (!text) return [];
  const tokens = String(text).split(/,|\bet\b/i).map((t) => t.trim()).filter(Boolean);
  const descs = tokens.map((t) => {
    const low = stripAccents(t);
    const days = (low.match(/\b\d{1,2}\b/g) || [])   // l'année (4 chiffres) n'est pas captée
      .map((n) => parseInt(n, 10)).filter((n) => n >= 1 && n <= 31);
    const yearM = low.match(/\b(\d{4})\b/);
    const months = monthsInText(low);
    const isRange = RANGE_RE.test(low) && days.length >= 2;
    return {
      days,
      month: months.length ? months[0] : -1,
      monthEnd: months.length ? months[months.length - 1] : -1,
      year: yearM ? parseInt(yearM[1], 10) : null,
      isRange,
    };
  });
  // Backfill mois/année : format condensé -> seul le dernier jeton les porte.
  for (let i = descs.length - 1; i >= 0; i--) {
    if (descs[i].month < 0 || descs[i].year == null) {
      for (let j = i + 1; j < descs.length; j++) {
        if (descs[j].month >= 0 && descs[j].year != null) {
          if (descs[i].month < 0) { descs[i].month = descs[j].month; descs[i].monthEnd = descs[j].month; }
          if (descs[i].year == null) descs[i].year = descs[j].year;
          break;
        }
      }
    }
  }
  const out = [];
  const seen = new Set();
  const push = (y, m, d) => {
    if (y == null || m < 0 || d < 1 || d > 31) return;
    const key = `${y}-${m}-${d}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push(new Date(y, m, d));
  };
  for (const p of descs) {
    if (p.year == null || p.month < 0 || !p.days.length) continue;
    if (p.isRange) {
      const d1 = p.days[0];
      const d2 = p.days[p.days.length - 1];
      const mEnd = p.monthEnd >= 0 ? p.monthEnd : p.month;
      let start = new Date(p.year, p.month, d1);
      let end = new Date(p.year, mEnd, d2);
      if (end < start) end = new Date(p.year + 1, mEnd, d2); // bascule d'année
      let guard = 0;
      const cur = new Date(start);
      while (cur <= end && guard < 62) {  // garde-fou anti-plage aberrante
        push(cur.getFullYear(), cur.getMonth(), cur.getDate());
        cur.setDate(cur.getDate() + 1);
        guard++;
      }
    } else {
      for (const d of p.days) push(p.year, p.month, d);
    }
  }
  return out.sort((a, b) => a - b);
}

// Champ de dates : saisie libre (repli) + calendrier multi-dates qui remplit
// automatiquement le texte au format FR. Le calendrier est TOUJOURS synchronisé
// avec le texte (source de vérité unique) : plus de dates « figées ».
export function DatesPicker({ value, onChange, onDatesChange, label = "Dates proposées / confirmées", helperText, testId = "dates" }) {
  const [open, setOpen] = useState(false);

  const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

  // La sélection du calendrier est dérivée du texte courant (pas d'état interne divergent).
  const dates = useMemo(() => parseDatesFr(value), [value]);

  // Émet les dates ISO à CHAQUE changement (saisie texte OU calendrier), pour que
  // le parent dispose toujours des créneaux structurés (ex : positionnement planning).
  useEffect(() => {
    if (onDatesChange) onDatesChange(dates.map(toISO));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const applyDates = (ds) => {
    const arr = Array.isArray(ds) ? [...ds].sort((a, b) => a - b) : [];
    onChange(formatDatesFr(arr));
    if (onDatesChange) onDatesChange(arr.map(toISO));
  };

  return (
    <div>
      {label && <Label className="text-xs">{label}</Label>}
      <div className={`${label ? "mt-1 " : ""}flex items-center gap-2`}>
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="ex : lundi 4 et mardi 5 mai 2026"
          className="h-9 flex-1"
          data-testid={testId}
        />
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button type="button" variant="outline"
              className="h-9 shrink-0 border-[var(--border-soft)] bg-white px-2.5"
              data-testid={`${testId}-calendar-btn`} title="Choisir sur le calendrier">
              <CalendarDays className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="end" data-testid={`${testId}-popover`}>
            <Calendar mode="multiple" locale={fr} selected={dates} onSelect={applyDates} data-testid={`${testId}-calendar`} />
            <div className="flex items-center justify-between gap-2 border-t border-[var(--border-soft)] p-2">
              <button type="button" onClick={() => applyDates([])}
                className="text-xs font-semibold text-muted-ink hover:text-ink" data-testid={`${testId}-clear`}>
                Effacer
              </button>
              <span className="text-[11px] text-muted-ink">
                {dates.length > 0 ? `${dates.length} date${dates.length > 1 ? "s" : ""}` : "Sélectionnez une ou plusieurs dates"}
              </span>
              <Button type="button" size="sm" onClick={() => setOpen(false)}
                className="h-8 bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid={`${testId}-ok`}>
                OK
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      </div>
      {helperText && <p className="mt-1 text-[10px] text-muted-ink">{helperText}</p>}
      {value && value.trim() && dates.length === 0 && (
        <p className="mt-1 flex items-center gap-1 text-[10px] font-semibold text-[#B5470F]"
          data-testid={`${testId}-unrecognized`}>
          <CalendarDays className="h-3 w-3 shrink-0" />
          Dates non reconnues — sélectionnez-les sur le calendrier pour garantir leur pose sur le planning.
        </p>
      )}
    </div>
  );
}
